import React, { useMemo, useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import SimpleTokenDisplay from './SimpleTokenDisplay';
import {
  getTokensForCard,
  getTokensForCommunity,
  getTokensForSubclass,
  updateTokenCount,
  activateWildFortress,
  activateThornSkin,
  refreshTokens
} from '../services/SimpleTokenService';

/**
 * Domain Card Tokens Component
 * Shows and manages tokens/resources associated with a specific domain card
 *
 * @param {Object} props
 * @param {Object} props.card - Domain card data
 * @param {Object} props.character - Character data
 * @param {function} props.updateCharacter - Function to update character
 * @param {string} props.size - Display size: 'tiny', 'small', 'normal'
 * @param {boolean} props.showEmpty - Show tokens even when at 0
 * @param {string} props.spellcastTrait - Optional spellcast trait for accurate token counts
 * @param {string} props.view - 'preview' (card on sheet) or 'details' (card modal)
 */
const DomainCardTokens = ({
  card,
  character,
  updateCharacter,
  size = 'small',
  showEmpty = false,
  spellcastTrait = null,
  view = 'details',
  setPendingSpellAttack = null, // For triggering spell attacks from domain cards
  setParentActiveTab = null, // For switching to combat tab
}) => {
  if (!card || !character) return null;

  // Get tokens that this card should have
  const expectedTokens = card.id.startsWith('community-')
    ? getTokensForCommunity(card.id.replace('community-', ''), character)
    : card.id.startsWith('subclass-')
    ? getTokensForSubclass(card.id.replace('subclass-', ''), character)
    : getTokensForCard(card.id, character, spellcastTrait);

  // Get existing resources for this card
  const cardResources = character.cardResources?.[card.id] || {};

  // Combine expected tokens with actual stored values
  const allResources = expectedTokens.map(expectedToken => {
    const existingToken = cardResources[expectedToken.id];

    return {
      id: expectedToken.id,
      name: expectedToken.name,
      type: expectedToken.type,
      current: existingToken?.current ?? expectedToken.current,
      maximum: expectedToken.maximum,
      refreshTrigger: expectedToken.refreshTrigger,
      requiresActivation: existingToken?.requiresActivation ?? expectedToken.requiresActivation
    };
  });

  // State for activation messages
  const [activationMessage, setActivationMessage] = useState('');

  // State for Unleash Chaos token selection
  const [selectedChaosTokens, setSelectedChaosTokens] = useState(0);

  // Ref and state for Thorn Skin roll result
  const timeoutRef = useRef(null);
  const lastThornRoll = cardResources?.['thorn-tokens']?.lastRoll || null;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Cards with custom UI that handle their own token display
  // Map of card ID → resource ID to hide from generic display
  const CUSTOM_UI_TOKEN_FILTER = {
    'wild-fortress': 'fortress-hp',
    'thorn-skin': 'thorn-tokens',
    'fane-of-the-wilds': 'fane-tokens',
    'strategic-approach': 'strategy-tokens',
    'restoration': 'restoration-tokens',
    'inspirational-words': 'inspiration-tokens',
    'twilight-toll': 'toll-tokens',
    'unleash-chaos': 'chaos-tokens',
  };

  // Filter out resources that have custom UI, and exclude empty tokens
  const visibleResources = allResources.filter(resource => {
    if (CUSTOM_UI_TOKEN_FILTER[card.id] === resource.id) return false;

    if (showEmpty) return true;
    // Always show if there's a maximum (potential tokens), even if current is 0
    return resource.maximum > 0;
  });

  // Handle Wild Fortress activation
  const handleWildFortressActivate = () => {
    if (character.hope >= 2) {
      const updatedCharacter = activateWildFortress(character);
      updateCharacter(updatedCharacter);
      setActivationMessage('🏰 Wild Fortress activated!');
      setTimeout(() => setActivationMessage(''), 3000);
    } else {
      setActivationMessage('❌ Need 2 Hope to activate!');
      setTimeout(() => setActivationMessage(''), 3000);
    }
  };

  // Handle Thorn Skin activation
  const handleThornSkinActivate = () => {
    if (character.hope >= 1) {
      const updatedCharacter = activateThornSkin(character);
      updateCharacter(updatedCharacter);
      setActivationMessage('🌿 Thorn Skin activated!');
      setTimeout(() => setActivationMessage(''), 3000);
    } else {
      setActivationMessage('❌ Need 1 Hope to activate!');
      setTimeout(() => setActivationMessage(''), 3000);
    }
  };

  // Handle session refresh for Thorn Skin
  const handleSessionRefresh = () => {
    // Clear tokens to require reactivation
    let updatedCharacter = { ...character };
    if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
    if (!updatedCharacter.cardResources['thorn-skin']) updatedCharacter.cardResources['thorn-skin'] = {};
    if (updatedCharacter.cardResources['thorn-skin']['thorn-tokens']) {
      updatedCharacter.cardResources['thorn-skin']['thorn-tokens'].current = 0;
      updatedCharacter.cardResources['thorn-skin']['thorn-tokens'].requiresActivation = true;
    }

    updateCharacter(updatedCharacter);
    setActivationMessage('🔄 New session! Thorn Skin needs reactivation.');
    setTimeout(() => setActivationMessage(''), 3000);
  };

  return (
    <div className="flex flex-col gap-2 mt-2">
      {/* Special UI for Wild Fortress */}
      {card.id === 'wild-fortress' && (
        <div className="flex flex-col gap-2">
          {view === 'preview' ? (
            // Card preview: Show activation button or tokens with Mark Damage
            <div className="flex items-center gap-2">
              {cardResources['fortress-hp']?.requiresActivation === false ? (
                <>
                  <span className="text-xs text-gray-400">Thresholds: 15/30</span>
                  <SimpleTokenDisplay
                    current={3 - (cardResources['fortress-hp'].current || 0)}
                    maximum={3}
                    size="small"
                    showEmpty={true}
                  />
                  {cardResources['fortress-hp'].current < 3 ? (
                    <button
                      onClick={() => {
                        const newValue = (cardResources['fortress-hp'].current || 0) + 1;
                        let updatedCharacter;

                        if (newValue >= 3) {
                          // Fortress destroyed - reset to require activation
                          updatedCharacter = { ...character };
                          if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                          if (!updatedCharacter.cardResources['wild-fortress']) updatedCharacter.cardResources['wild-fortress'] = {};
                          delete updatedCharacter.cardResources['wild-fortress']['fortress-hp'];
                          setActivationMessage('💥 Wild Fortress destroyed! Must reactivate.');
                          setTimeout(() => setActivationMessage(''), 3000);
                        } else {
                          updatedCharacter = updateTokenCount(
                            character,
                            card.id,
                            'fortress-hp',
                            newValue
                          );
                        }
                        updateCharacter(updatedCharacter);
                      }}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      title="Mark 1 HP on fortress"
                    >
                      Mark Damage
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        // Reset destroyed fortress
                        const updatedCharacter = { ...character };
                        if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                        if (!updatedCharacter.cardResources['wild-fortress']) updatedCharacter.cardResources['wild-fortress'] = {};
                        delete updatedCharacter.cardResources['wild-fortress']['fortress-hp'];
                        updateCharacter(updatedCharacter);
                        setActivationMessage('💥 Fortress destroyed! Ready to reactivate.');
                        setTimeout(() => setActivationMessage(''), 3000);
                      }}
                      className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                      title="Fortress destroyed - click to reset"
                    >
                      Reset
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={handleWildFortressActivate}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  title="Activate Wild Fortress (Spellcast 13 + 2 Hope)"
                >
                  Activate (2 Hope)
                </button>
              )}
            </div>
          ) : (
            // Card details: Show full controls
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Thresholds: 15/30</span>
              </div>
              <div className="flex items-center gap-2">
                {/* When activated, requiresActivation becomes false */}
                {cardResources['fortress-hp']?.requiresActivation === false ? (
                  <>
                    <SimpleTokenDisplay
                      current={3 - (cardResources['fortress-hp'].current || 0)}
                      maximum={3}
                      size="small"
                      showEmpty={true}
                    />
                    {cardResources['fortress-hp'].current < 3 ? (
                      <button
                        onClick={() => {
                          const newValue = (cardResources['fortress-hp'].current || 0) + 1;
                          let updatedCharacter;

                          if (newValue >= 3) {
                            // Fortress destroyed - reset to require activation
                            updatedCharacter = { ...character };
                            if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                            if (!updatedCharacter.cardResources['wild-fortress']) updatedCharacter.cardResources['wild-fortress'] = {};
                            delete updatedCharacter.cardResources['wild-fortress']['fortress-hp'];
                            setActivationMessage('💥 Wild Fortress destroyed! Must reactivate.');
                            setTimeout(() => setActivationMessage(''), 3000);
                          } else {
                            updatedCharacter = updateTokenCount(
                              character,
                              card.id,
                              'fortress-hp',
                              newValue
                            );
                          }
                          updateCharacter(updatedCharacter);
                        }}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                        title="Mark 1 HP on fortress"
                      >
                        Mark Damage
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          // Reset destroyed fortress
                          const updatedCharacter = { ...character };
                          if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                          if (!updatedCharacter.cardResources['wild-fortress']) updatedCharacter.cardResources['wild-fortress'] = {};
                          delete updatedCharacter.cardResources['wild-fortress']['fortress-hp'];
                          updateCharacter(updatedCharacter);
                          setActivationMessage('💥 Fortress destroyed! Ready to reactivate.');
                          setTimeout(() => setActivationMessage(''), 3000);
                        }}
                        className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                        title="Fortress destroyed - click to reset"
                      >
                        Reset
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleWildFortressActivate}
                    className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    title="Activate Wild Fortress (Spellcast 13 + 2 Hope)"
                  >
                    Activate (2 Hope)
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Special UI for Thorn Skin */}
      {card.id === 'thorn-skin' && (
        <div className="flex items-center gap-2">
          <SimpleTokenDisplay
            current={cardResources['thorn-tokens']?.current || 0}
            maximum={character.traits?.Finesse || character.traits?.Presence ||
                    character.traits?.Instinct || character.traits?.Knowledge || 4}
            size="small"
            showEmpty={true}
          />
          {/* Show roll result or appropriate button - keep showing roll even if tokens reach 0 */}
          {lastThornRoll ? (
            <button
              disabled
              className="px-2 py-1 text-xs bg-green-600 text-white rounded"
            >
              -{lastThornRoll} dmg!
            </button>
          ) : (!cardResources['thorn-tokens'] || cardResources['thorn-tokens'].current === 0) ? (
            <button
              onClick={handleThornSkinActivate}
              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              title="Activate Thorn Skin (1 Hope)"
            >
              Activate (1 Hope)
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  const thornTokens = cardResources['thorn-tokens'];
                  if (thornTokens.current > 0) {
                    // Roll d4 for damage reduction
                    const reduction = Math.ceil(Math.random() * 4);

                    // Update character with roll result stored in token data
                    let updatedCharacter = updateTokenCount(
                      character,
                      card.id,
                      'thorn-tokens',
                      thornTokens.current - 1
                    );

                    // Store the roll result in the token data
                    if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                    if (!updatedCharacter.cardResources[card.id]) updatedCharacter.cardResources[card.id] = {};
                    if (updatedCharacter.cardResources[card.id]['thorn-tokens']) {
                      updatedCharacter.cardResources[card.id]['thorn-tokens'].lastRoll = reduction;
                    }

                    updateCharacter(updatedCharacter);
                    setActivationMessage(`🌿 Reduced ${reduction} damage!`);

                    // Clear any existing timeout
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                    }

                    // Clear the roll display after 2 seconds
                    // Use updatedCharacter to ensure we have the latest state
                    timeoutRef.current = setTimeout(() => {
                      const clearedCharacter = { ...updatedCharacter };
                      if (clearedCharacter.cardResources?.[card.id]?.['thorn-tokens']) {
                        delete clearedCharacter.cardResources[card.id]['thorn-tokens'].lastRoll;
                        updateCharacter(clearedCharacter);
                      }
                      timeoutRef.current = null;
                    }, 2000);
                  }
                }}
                className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                title="Spend 1 token to reduce damage by d4"
              >
                Spend (d4)
              </button>
              {view === 'details' && (
                <button
                  onClick={handleSessionRefresh}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  title="New session - refresh tokens"
                >
                  New Session
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Special UI for Fane of the Wilds */}
      {card.id === 'fane-of-the-wilds' && (
        <div className="flex items-center gap-2">
          <SimpleTokenDisplay
            current={cardResources['fane-tokens']?.current || 0}
            maximum={3}
            size="small"
            showEmpty={true}
          />
          {view === 'details' && (
            <button
              onClick={() => {
                const updatedCharacter = refreshTokens(character, 'long-rest', spellcastTrait);
                updateCharacter(updatedCharacter);
                setActivationMessage('🌲 New session! Fane tokens refreshed.');
                setTimeout(() => setActivationMessage(''), 3000);
              }}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              title="New session - refresh tokens"
            >
              New Session
            </button>
          )}
        </div>
      )}

      {/* Strategic Approach UI removed - now handled by TripleButtonPattern */}

      {/* Special UI for Restoration */}
      {card.id === 'restoration' && (
        <div className="flex items-center gap-2">
          <SimpleTokenDisplay
            current={cardResources['restoration-tokens']?.current || 0}
            maximum={allResources.find(r => r.id === 'restoration-tokens')?.maximum || 2}
            size="small"
            showEmpty={true}
          />
          {(cardResources['restoration-tokens']?.current || 0) > 0 && (
            <button
              onClick={() => {
                const currentTokens = cardResources['restoration-tokens']?.current || 0;
                if (currentTokens > 0) {
                  const updatedCharacter = updateTokenCount(
                    character,
                    card.id,
                    'restoration-tokens',
                    currentTokens - 1
                  );
                  updateCharacter(updatedCharacter);
                  setActivationMessage('💚 Restoration token spent!');
                  setTimeout(() => setActivationMessage(''), 2000);
                }
              }}
              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              title="Spend 1 Restoration token"
            >
              Spend
            </button>
          )}
          {view === 'details' && (
            <button
              onClick={() => {
                // Get fresh token definition with correct spellcast trait
                const freshTokens = expectedTokens.find(t => t.id === 'restoration-tokens');
                if (freshTokens) {
                  let updatedCharacter = { ...character };
                  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                  if (!updatedCharacter.cardResources[card.id]) updatedCharacter.cardResources[card.id] = {};

                  // Update with fresh maximum value
                  updatedCharacter.cardResources[card.id]['restoration-tokens'] = {
                    ...freshTokens,
                    current: freshTokens.maximum // Refresh to maximum
                  };

                  updateCharacter(updatedCharacter);
                  setActivationMessage('🔄 New session! Restoration tokens refreshed.');
                  setTimeout(() => setActivationMessage(''), 3000);
                }
              }}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              title="New session - refresh tokens"
            >
              New Session
            </button>
          )}
        </div>
      )}

      {/* Special UI for Unleash Chaos */}
      {card.id === 'unleash-chaos' && (() => {
        const chaosTokens = cardResources['chaos-tokens']?.current || 0;
        const maxTokens = allResources.find(r => r.id === 'chaos-tokens')?.maximum || (spellcastTrait ? character?.traits?.[spellcastTrait] : 2) || 2;
        const stress = character?.stress || 0;
        const maxStress = character?.maxStress || 8;
        const canReplenish = stress < maxStress && chaosTokens < maxTokens;
        // Clamp selected tokens to available tokens
        const effectiveSelected = Math.min(selectedChaosTokens, chaosTokens);
        const canUnleash = effectiveSelected > 0 && setPendingSpellAttack;

        const handleReplenish = () => {
          if (stress >= maxStress) return;
          if (chaosTokens >= maxTokens) return;

          let updatedCharacter = { ...character, stress: stress + 1 };
          if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
          if (!updatedCharacter.cardResources[card.id]) updatedCharacter.cardResources[card.id] = {};
          updatedCharacter.cardResources[card.id]['chaos-tokens'] = {
            id: 'chaos-tokens',
            name: 'Chaos Tokens',
            type: 'token',
            current: maxTokens,
            maximum: maxTokens,
            refreshTrigger: 'session'
          };
          updateCharacter(updatedCharacter);
          toast.success(`Tokens replenished to ${maxTokens}!`, { duration: 2000, icon: '😰' });
        };

        const handleUnleash = () => {
          if (!canUnleash) return;

          let updatedCharacter = { ...character };
          if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
          if (!updatedCharacter.cardResources[card.id]) updatedCharacter.cardResources[card.id] = {};
          updatedCharacter.cardResources[card.id]['chaos-tokens'] = {
            id: 'chaos-tokens',
            name: 'Chaos Tokens',
            type: 'token',
            current: chaosTokens - effectiveSelected,
            maximum: maxTokens,
            refreshTrigger: 'session'
          };
          updateCharacter(updatedCharacter);

          setPendingSpellAttack({
            difficulty: null,
            damage: `${effectiveSelected}d10`,
            damageType: 'magic',
            spellName: 'Unleash Chaos',
            cardKey: 'unleash-chaos',
          });

          setSelectedChaosTokens(0);
          if (setParentActiveTab) {
            setParentActiveTab('combat');
          }
        };

        return (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Token selector - inline clickable coins */}
            <div className="flex rounded overflow-hidden border border-purple-600">
              <div className="px-2 py-1 bg-purple-900/20 flex items-center gap-0.5 h-6">
                {chaosTokens > 0 ? (
                  Array.from({ length: chaosTokens }, (_, i) => {
                    const tokenIndex = i + 1;
                    const isSelected = tokenIndex <= effectiveSelected;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedChaosTokens(tokenIndex)}
                        className={`text-sm leading-none transition-all duration-150 hover:scale-110 ${
                          isSelected ? 'brightness-100' : 'brightness-50 hover:brightness-75'
                        }`}
                        title={`${isSelected ? 'Selected' : 'Click to select'} ${tokenIndex}`}
                      >
                        🪙
                      </button>
                    );
                  })
                ) : (
                  <span className="text-xs text-gray-400 px-1">Empty</span>
                )}
              </div>
              {canReplenish && (
                <>
                  <div className="w-px bg-purple-600" />
                  <button
                    onClick={handleReplenish}
                    className="px-2 py-1 text-xs bg-purple-900/20 text-purple-400 hover:bg-purple-900/40"
                    title="Mark 1 Stress to replenish tokens"
                  >
                    🔄 (1S)
                  </button>
                </>
              )}
            </div>

            {/* Unleash button */}
            <button
              onClick={handleUnleash}
              disabled={!canUnleash}
              className={`px-3 py-1 text-xs rounded font-semibold transition-all border ${
                canUnleash
                  ? 'bg-purple-900/20 text-purple-400 border-purple-600 hover:bg-purple-900/40'
                  : 'bg-gray-800 text-gray-500 border-gray-600 cursor-not-allowed'
              }`}
              title={effectiveSelected === 0 ? 'Select tokens first' : `Spellcast → ${effectiveSelected}d10`}
            >
              ⚡ {effectiveSelected > 0 ? `${effectiveSelected}d10` : 'Select'}
            </button>

            {/* Free session refresh - only in details view */}
            {view === 'details' && (
              <button
                onClick={() => {
                  // Directly set tokens to max (free session refresh)
                  let updatedCharacter = { ...character };
                  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
                  if (!updatedCharacter.cardResources[card.id]) updatedCharacter.cardResources[card.id] = {};
                  updatedCharacter.cardResources[card.id]['chaos-tokens'] = {
                    id: 'chaos-tokens',
                    name: 'Chaos Tokens',
                    type: 'token',
                    current: maxTokens,
                    maximum: maxTokens,
                    refreshTrigger: 'session'
                  };
                  updateCharacter(updatedCharacter);
                  setSelectedChaosTokens(0);
                  toast.success(`Session start! ${maxTokens} tokens ready.`, { duration: 2000, icon: '✨' });
                }}
                className="px-2 py-1 text-xs bg-blue-900/20 text-blue-400 border border-blue-600 rounded hover:bg-blue-900/40"
                title="New session - free token refresh"
              >
                🆕 Session
              </button>
            )}
          </div>
        );
      })()}

      {/* Special UI for Inspirational Words */}
      {card.id === 'inspirational-words' && (
        <div className="flex items-center gap-2">
          <SimpleTokenDisplay
            current={cardResources['inspiration-tokens']?.current || 0}
            maximum={character.traits?.Presence || 1}
            size="small"
            showEmpty={true}
          />
          {(cardResources['inspiration-tokens']?.current || 0) > 0 && (
            <button
              onClick={() => {
                const currentTokens = cardResources['inspiration-tokens']?.current || 0;
                if (currentTokens > 0) {
                  const updatedCharacter = updateTokenCount(
                    character,
                    card.id,
                    'inspiration-tokens',
                    currentTokens - 1
                  );
                  updateCharacter(updatedCharacter);
                  setActivationMessage('✨ Inspiration token spent!');
                  setTimeout(() => setActivationMessage(''), 2000);
                }
              }}
              className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
              title="Spend 1 Inspiration token"
            >
              Spend
            </button>
          )}
          <button
            onClick={() => {
              const updatedCharacter = refreshTokens(character, 'long-rest', spellcastTrait);
              updateCharacter(updatedCharacter);
              setActivationMessage('🔄 New session! Inspiration tokens refreshed.');
              setTimeout(() => setActivationMessage(''), 3000);
            }}
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            title="New session - refresh tokens"
          >
            New Session
          </button>
        </div>
      )}

      {/* Special UI for Twilight Toll */}
      {card.id === 'twilight-toll' && (
        <div className="flex items-center gap-2">
          <SimpleTokenDisplay
            current={cardResources['toll-tokens']?.current || 0}
            maximum={cardResources['toll-tokens']?.current || 0}
            size="small"
            showEmpty={true}
          />
          <button
            onClick={() => {
              const currentTokens = cardResources['toll-tokens']?.current || 0;
              const updatedCharacter = updateTokenCount(
                character,
                card.id,
                'toll-tokens',
                currentTokens + 1
              );
              updateCharacter(updatedCharacter);
              setActivationMessage('🔔 Toll token added!');
              setTimeout(() => setActivationMessage(''), 2000);
            }}
            className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
            title="Add 1 Toll token"
          >
            Add
          </button>
          {(cardResources['toll-tokens']?.current || 0) > 0 && (
            <button
              onClick={() => {
                const currentTokens = cardResources['toll-tokens']?.current || 0;
                if (currentTokens > 0) {
                  const updatedCharacter = updateTokenCount(
                    character,
                    card.id,
                    'toll-tokens',
                    currentTokens - 1
                  );
                  updateCharacter(updatedCharacter);
                  setActivationMessage('🔔 Toll token spent!');
                  setTimeout(() => setActivationMessage(''), 2000);
                }
              }}
              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
              title="Spend 1 Toll token"
            >
              Spend
            </button>
          )}
        </div>
      )}

      {activationMessage && (
        <div className="text-xs text-yellow-300 bg-gray-800 p-1 rounded">
          {activationMessage}
        </div>
      )}

      {visibleResources.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {visibleResources.map(resource => (
          <div key={resource.id} className="flex items-center gap-1">
            <SimpleTokenDisplay
              current={resource.current}
              maximum={card.id === 'twilight-toll' && resource.id === 'toll-tokens' ? resource.current : resource.maximum}
              size={size}
              showEmpty={showEmpty}
              type={resource.type}
            />
            {/* Only show resource name if card has a name (not just inline display) */}
            {resource.name && card.name && (
              <span className="text-xs text-gray-400">{resource.name}</span>
            )}
          </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Quick Token Display Component
 * Minimal display for showing token counts without controls
 */
export const DomainCardTokenDisplay = ({ card, character, size = 'tiny' }) => {
  if (!card || !character) return null;

  const cardResources = character.cardResources?.[card.id] || {};
  const hasTokens = Object.keys(cardResources).some(key => cardResources[key].current > 0);

  if (!hasTokens) return null;

  return (
    <div className="flex items-center space-x-1 text-xs text-gray-500">
      {Object.keys(cardResources).map(resourceKey => {
        const resource = cardResources[resourceKey];
        if (resource.current === 0) return null;

        const typeIcon = {
          token: '🪙',
          usage: '⚡',
          counter: '🎯'
        }[resource.type] || '🔢';

        return (
          <span key={resourceKey} className="inline-flex items-center space-x-0.5">
            <span>{typeIcon}</span>
            <span>{resource.current}{resource.maximum ? `/${resource.maximum}` : ''}</span>
          </span>
        );
      })}
    </div>
  );
};

export default DomainCardTokens;